import React, { useCallback, useEffect, useRef, useState } from 'react';

/* ==================================================================
   Modal
   ------------------------------------------------------------------
   One motion system for every dialog: the overlay fades and blurs,
   the card scales 0.98 -> 1. Closing plays the reverse before the
   tree unmounts, which is what AnimatePresence buys elsewhere - here
   it is a `closing` flag plus one timeout matched to the CSS.

   Also handles the things a dialog has to handle regardless of looks:
   Escape closes it, the page behind it stops scrolling, and clicking
   the backdrop closes unless `dismissible` says otherwise.
   ================================================================== */

const EXIT_MS = 150; /* keep in sync with --ui-fast in motion.css */

export default function Modal({
  open,
  onClose,
  children,
  maxWidth = 560,
  dismissible = true,
  showClose = true,
  labelledBy,
  className = '',
  cardStyle,
}) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (open) {
      if (timer.current) clearTimeout(timer.current);
      setClosing(false);
      setMounted(true);
    } else if (mounted) {
      setClosing(true);
      timer.current = setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, EXIT_MS);
    }
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [open, mounted]);

  const requestClose = useCallback(() => {
    if (dismissible && onClose) onClose();
  }, [dismissible, onClose]);

  /* Escape + scroll lock, only while actually open. */
  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [mounted, requestClose]);

  if (!mounted) return null;

  return (
    <div
      className="ui-modal-overlay"
      data-closing={closing ? 'true' : 'false'}
      onClick={requestClose}
      role="presentation"
    >
      <div
        className={`ui-modal-card ${className}`.trim()}
        style={{ maxWidth, ...cardStyle }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
      >
        {showClose && onClose && (
          <button type="button" className="ui-modal-x" onClick={onClose} aria-label="Close">
            &times;
          </button>
        )}
        {children}
      </div>
    </div>
  );
}
